import { onResize, onMouseMove } from "@js/throttle.js"

const twConfig = __TAILWIND_CONFIG__;

export function generateGraphicGrid() {
	const grids = document.querySelectorAll(".grid-graphic");

	grids.forEach((grid) => {
		grid.innerHTML = "";
		grid.style.removeProperty("grid-template-columns");
		grid.style.removeProperty("grid-template-rows");

		const computedStyle = window.getComputedStyle(grid);
		const gridColumns = computedStyle.getPropertyValue('grid-template-columns');
		const columnCount = gridColumns.split(' ').length;
		const gridRows = computedStyle.getPropertyValue('grid-template-rows');
		const rowCount = gridRows.split(' ').length;

		const gridBlocks = columnCount * rowCount;

		// Use DocumentFragment to batch DOM operations
		const fragment = document.createDocumentFragment();
		for (let i = 0; i < gridBlocks; i++) {
			const div = document.createElement('div');
			fragment.appendChild(div);
		}
		grid.appendChild(fragment);
	});

}

export function generateGridHashMarks() {
	const grids = document.querySelectorAll(".grid-hash-marks");

	grids.forEach((grid) => {
		grid.style.removeProperty("grid-template-columns");
		grid.style.removeProperty("grid-template-rows");

		const querySelector = '.grid-hash-marks > div[class^="grid-top"], .grid-hash-marks > div[class^="grid-middle"], .grid-hash-marks > div[class^="grid-bottom"], .grid-hash-marks > .grid-border';
		const hashMarksAndBorders = grid.querySelectorAll(querySelector);
		hashMarksAndBorders.forEach(div => div.remove());

		grid.querySelectorAll('.grid-blank').forEach(div => div.remove());

		const computedStyle = window.getComputedStyle(grid);
		const gridColumns = computedStyle.getPropertyValue('grid-template-columns');
		const columnCount = gridColumns.split(' ').length;
		const gridRows = computedStyle.getPropertyValue('grid-template-rows');
		const rowCount = gridRows.split(' ').length;

		const gridWidth = columnCount * 2 + 1;
		const gridHeight = rowCount * 2 + 1;

		const existingChildren = [...grid.children];
		let existingChildIterator = 0;
		const gridItemCount = existingChildren.length;

		const gridColsValue = getGridColsValue(grid);
		if (gridColsValue) {
			grid.style.setProperty('grid-template-columns', addPixelsBetween(gridColsValue));
		}

		let gridRowsValue = getGridRowsValue(grid);
		if (!gridRowsValue || gridRowsValue == 'none') {
			const rowCount = Math.ceil(gridItemCount / columnCount);
			const autoRows = getResponsiveGridValue(grid, 'auto-rows');
			if (autoRows) {
				gridRowsValue = Array(rowCount).fill().map((_, i) => autoRows[i % autoRows.length])
			} else {
				gridRowsValue = Array(rowCount).fill('minmax(0px, 1fr)');
			}
		}
		grid.style.setProperty('grid-template-rows', addPixelsBetween(gridRowsValue));

		grid.innerHTML = "";

		// Loop through rows and columns to generate the grid
		for (let row = 0; row < gridHeight; row++) {
			for (let col = 0; col < gridWidth; col++) {
				let div = null;

				// Determine class based on position
				if (row === 0) {
					// Top row
					div = document.createElement('div');
					if (col === 0) div.className = "grid-top-left";
					else if (col === gridWidth - 1) div.className = "grid-top-right";
					else if (col % 2 === 0) div.className = "grid-top-middle";
					else if (col % 2 === 1) div.className = "grid-border grid-border-x";
				} else if (row === gridHeight - 1) {
					// Bottom row
					div = document.createElement('div');
					if (col === 0) div.className = "grid-bottom-left";
					else if (col === gridWidth - 1) div.className = "grid-bottom-right";
					else if (col % 2 === 0) div.className = "grid-bottom-middle";
					else if (col % 2 === 1) div.className = "grid-border grid-border-x";
				} else if (row % 2 === 0) {
					// Middle rows
					div = document.createElement('div');
					if (col === 0) div.className = "grid-middle-left";
					else if (col === gridWidth - 1) div.className = "grid-middle-right";
					else if (col % 2 === 0) div.className = "grid-middle";
					else if (col % 2 === 1) div.className = "grid-border grid-border-x grid-border-internal";
				} else if (row % 2 === 1 && col % 2 === 0) {
					// Middle rows
					div = document.createElement('div');
					div.className = "grid-border grid-border-y";
					if (col !== 0 && col !== gridWidth - 1) div.className += " grid-border-internal";
				} else {
					div = existingChildren[existingChildIterator];
					if (!div) {
						div = document.createElement('div');
						div.className = "grid-blank";
					}
					existingChildIterator++;
				}
				// Append div to the grid container
				grid.appendChild(div);
			}
		}

		generateGridHashMarkBounds();
	});

	// Function to check if mouse is within the bounds of a given rectangle
	function isHoveringOverRect(rect, x, y) {
		return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
	}

	onMouseMove((e) => {
		if (!window.gridHashMarks) return;

		const scrollX = window.scrollX;
		const scrollY = window.scrollY;

		// Check for hover effects on the background divs
		window.gridHashMarks.forEach((el, index) => {
			const rect = window.gridHashMarksBounds[index];

			const mouseX = rect.fixed ? e.clientX : e.clientX + scrollX;
			const mouseY = rect.fixed ? e.clientY : e.clientY + scrollY;

			const isHovering = isHoveringOverRect(rect, mouseX, mouseY);

			if (isHovering) {
				el.classList.add('grid-hover');
				setTimeout(() => {
					el.classList.remove('grid-hover');
				}, 1000);
			}
		});
	});

	// Update bounding rects if the window is resized
	onResize(() => {
		// Invalidate grid size cache on resize
		cachedGridSize = null;
		generateGridHashMarkBounds();
	});
}

export function generateGridHashMarkBounds() {
	const querySelector = '.grid-hash-marks > div[class^="grid-top"], .grid-hash-marks > div[class^="grid-middle"], .grid-hash-marks > div[class^="grid-bottom"]';

	const hashMarks = document.querySelectorAll(querySelector);

	window.gridHashMarks = hashMarks;
	window.gridHashMarksBounds = [];

	// Cache scroll values to avoid repeated access
	const scrollY = window.scrollY;
	const scrollX = window.scrollX;

	window.gridHashMarksBounds.length = 0;
	hashMarks.forEach(el => {
		const isParentFixed = el.closest('.grid-hash-marks')?.classList.contains('grid-hash-marks-fixed') || false;

		const elRect = el.getBoundingClientRect();
		window.gridHashMarksBounds.push({
			top: elRect.top + (isParentFixed ? 0 : scrollY),
			bottom: elRect.bottom + (isParentFixed ? 0 : scrollY),
			left: elRect.left + (isParentFixed ? 0 : scrollX),
			right: elRect.right + (isParentFixed ? 0 : scrollX),
			fixed: isParentFixed
		});
	});
}

function convertThemeSpacing(value) {
	// Extract theme spacing and replace while keeping surrounding context
	return value.replace(/theme\(spacing\.(.*?)\)/g, (match, spacing) => {
		return twConfig.theme.spacing[spacing] || match;
	});
}

function expandRepeat(value) {
	const repeatMatch = value.match(/repeat\((\d+),\s*((?:[^()]|\([^()]*\))+)\)/);
	if (!repeatMatch) return value;

	const [_, count, pattern] = repeatMatch;

	// Replace spaces within parentheses with temporary character
	const tempPattern = pattern.replace(/\([^)]+\)/g, match =>
		match.replace(/\s/g, '§')
	);

	// Split on spaces and restore original spaces
	const declarations = tempPattern.trim().split(' ').map(item =>
		item.replace(/§/g, ' ')
	);

	// More efficient array creation and flattening
	const finalArray = [];
	const countNum = parseInt(count);

	for (let i = 0; i < countNum; i++) {
		if (Array.isArray(declarations)) {
			finalArray.push(...declarations);
		} else {
			finalArray.push(declarations);
		}
	}

	return finalArray;
}

function getGridValue(element, type, screen = null, container = null) {
	const gridPrefix = (screen ? screen + ':' : '') + (container ? '@' + container + ':' : '') + `${type}-`;
	const gridClass = Array.from(element.classList).find(cls => cls.startsWith(gridPrefix));

	if (!gridClass) return null;

	const escapedGridPrefix = gridPrefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

	// Handle -none values (grid-cols or grid-rows)
	if (gridClass === `${gridPrefix}none`) return 'none';

	// Handle -auto values (auto-cols or auto-rows)
	if (gridClass === `${gridPrefix}auto`) return ['auto'];

	// Handle -fr values (auto-cols or auto-rows)
	if (gridClass === `${gridPrefix}fr`) return ['minmax(0, 1fr)'];

	// Handle simple numeric grid values (e.g., grid-cols-3 or grid-rows-3)
	const numericMatch = gridClass.match(new RegExp(`^${escapedGridPrefix}(\\d+)$`));
	if (numericMatch) {
		const count = parseInt(numericMatch[1]);
		return Array(count).fill('minmax(0px, 1fr)');
	}

	// Handle bracket notation
	const bracketMatch = gridClass.match(new RegExp(`${escapedGridPrefix}\\[(.*?)\\]`));

	if (!bracketMatch) return null;

	const spacedContent = bracketMatch[1].replace(/_/g, ' ');
	const arrayedContent = spacedContent.match(/\w+\([^()]*(?:\([^()]*\)[^()]*)*\)|[^\s]+/g);

	let finalArray = []

	arrayedContent.forEach(declaration => {
		declaration = declaration.includes('repeat(') ? expandRepeat(declaration) : declaration;
		if (Array.isArray(declaration)) {
			finalArray.push(...declaration);
		} else {
			finalArray.push(declaration);
		}
	});

	return finalArray.map(declaration => declaration.includes('theme(spacing.') ? convertThemeSpacing(declaration) : declaration);
}

function getResponsiveGridValue(element, type){
	const arbitraryScreens = Array.from(element.classList)
		.filter(cls => cls.startsWith('min-[') || cls.startsWith('max-['))
		.map(cls => cls.split(':')[0])
		.reduce((acc, cls) => {
			const value = cls.match(/\[(.*?)\]/)[1];
			acc[cls] = value;
			return acc;
		}, {});

	let screens = { ...twConfig.theme.screens };

	// Add max- versions of all screens
	{
		const maxScreens = {};
		for (const [key, value] of Object.entries(screens)) {
			if (!key.startsWith('max-')) {
				maxScreens[`max-${key}`] = value;
			}
		}
		Object.assign(screens, maxScreens);
	}

	// Add arbitrary screen values to screens object
	Object.assign(screens, arbitraryScreens);

	// Separate max and regular screens
	const maxScreens = Object.entries(screens).filter(([key]) => key.includes('max-'));
	const regularScreens = Object.entries(screens).filter(([key]) => !key.includes('max-'));

	// Sort both arrays by numeric value
	const sortByValue = (a, b) => parseFloat(a[1].replace('rem', '')) - parseFloat(b[1].replace('rem', ''));
	maxScreens.sort(sortByValue);
	regularScreens.sort(sortByValue);

	// Combine arrays and convert back to object
	screens = Object.fromEntries([...maxScreens, ...regularScreens]);

	let screenKeys = Object.keys(screens).reverse();

	const containers = twConfig.theme.containers;
	const containerKeys = Object.keys(containers).reverse();

	const currentWidth = window.innerWidth;
	const container = element.closest('.\\@container');
	const containerWidth = container ? container.getBoundingClientRect().width : null;

	let gridValue = null;
	for (const screen of screenKeys) {
		const isMax = screen.includes('max-');
		const screenSizePx = parseFloat(screens[screen]) * 16;
		if ((isMax && screenSizePx > currentWidth) || (!isMax && screenSizePx <= currentWidth)) {
			for (const container of containerKeys) {
				if (containerWidth && (parseFloat(containers[container]) * 16) <= containerWidth) {
					gridValue = getGridValue(element, type, screen, container);
					if (gridValue) break;
				}
			}
			if (gridValue) break;
			gridValue = getGridValue(element, type, screen);
			if (gridValue) break;
		}
	}

	if (!gridValue) {
		for (const container of containerKeys) {
			if (containerWidth && (parseFloat(containers[container]) * 16) <= containerWidth) {
				gridValue = getGridValue(element, type, null, container);
				if (gridValue) break
			}
		}
	}

	return gridValue || getGridValue(element, type);

}

function getGridColsValue(element) {
	return getResponsiveGridValue(element, 'grid-cols');
}

function getGridRowsValue(element) {
	return getResponsiveGridValue(element, 'grid-rows');
}

function addPixelsBetween(array) {
	// Add 1px between and around all values
	return ['1px', ...array.flatMap(val => [val, '1px'])].join(' ');
}

let cachedGridSize = null;

export function getGridHeight(element, type = '', tweak = 0) {
	// Cache grid size to avoid repeated DOM operations
	if (cachedGridSize === null) {
		const div = document.createElement('div');
		div.style.width = `var(--grid-size)`;
		div.style.flexShrink = 0;
		div.style.position = 'absolute';
		div.style.visibility = 'hidden';
		document.documentElement.appendChild(div);
		const value = div.getBoundingClientRect().width;
		document.documentElement.removeChild(div);
		cachedGridSize = parseFloat(value);
	}

	const gridSize = cachedGridSize;

	// Get the actual height of the element
	const actualHeight = element.getBoundingClientRect().height;

	const multiplier = Math.ceil(actualHeight / (gridSize + 1));
	let newHeight = multiplier * gridSize + (multiplier + tweak);

	// Calculate the next largest multiple of grid size
	if (type == 'even') {
		// Get next even grid size
		if (multiplier % 2 !== 0) {
			newHeight += gridSize + 1;
		}
	} else if (type == 'odd') {
		// Get next odd grid size
		if (multiplier % 2 === 0) {
			newHeight += gridSize + 1;
		}
	}

	return newHeight;
}

export function alignHeightWithGrid() {
	// Define classes and their corresponding style properties
	// `tweak` allows for including the grid lines in the height calculation
	const heightClasses = [
		{ class: 'h-gsm', property: 'height', tweak: -1 },
		{ class: 'h-gs', property: 'height', tweak: 0 },
		{ class: 'h-gsp', property: 'height', tweak: 1 },
		{ class: 'min-h-gsm', property: 'min-height', tweak: -1 },
		{ class: 'min-h-gs', property: 'min-height', tweak: 0 },
		{ class: 'min-h-gsp', property: 'min-height', tweak: 1 },
	];

	heightClasses.forEach(({ class: className, property, tweak }) => {
		// Get all elements with the current class
		const elements = document.querySelectorAll(`.${className}`);

		if (!elements.length) return;

		elements.forEach(element => {
			element.style.removeProperty(property);
		});

		elements.forEach(element => {
			const newHeight = getGridHeight(element, '', tweak);

			// Set the new height/min-height
			element.style.setProperty(property, `${newHeight}px`);
		});
	});
}
